import { useForm } from 'react-hook-form';
import { Clock, User } from 'lucide-react';
import { Modal, Button } from '@components/ui';
import { calismaDurumlari } from '@mocks/puantaj';
import { mockPersonnel, getRoleLabel } from '@mocks/personel';

/**
 * PuantajModal - Puantaj kaydı ekleme/düzenleme modalı
 */
export default function PuantajModal({ isOpen, onClose, onSubmit, kayit, tarih }) {
    const isEdit = !!kayit;

    const { register, handleSubmit, watch, formState: { errors }, reset } = useForm({
        defaultValues: kayit || {
            tarih: tarih || new Date().toISOString().split('T')[0],
            personelId: '',
            baslangic: '08:00',
            bitis: '17:00',
            molaSaat: 1,
            durum: 'calisti',
            not: ''
        }
    });

    const selectedDurum = watch('durum');

    const onFormSubmit = (data) => {
        const personel = mockPersonnel.find(p => p.id === data.personelId);
        const baslangicSaat = parseFloat(data.baslangic.split(':')[0]) + parseFloat(data.baslangic.split(':')[1]) / 60;
        const bitisSaat = parseFloat(data.bitis.split(':')[0]) + parseFloat(data.bitis.split(':')[1]) / 60;
        const brutSaat = bitisSaat - baslangicSaat;
        const calismaSaat = Math.max(0, brutSaat - parseFloat(data.molaSaat));
        const fazlaMesai = Math.max(0, calismaSaat - 8);

        onSubmit({
            ...data,
            personelAdi: personel?.name || '',
            gorev: personel ? getRoleLabel(personel.role) : '',
            calismaSaat: data.durum === 'calisti' || data.durum === 'yarim_gun' ? calismaSaat : 0,
            fazlaMesai: data.durum === 'calisti' ? fazlaMesai : 0,
            molaSaat: parseFloat(data.molaSaat)
        });
        reset();
        onClose();
    };

    const inputClass = "w-full px-3 py-2 bg-bg-tertiary border border-border-light rounded-lg text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent text-sm";
    const labelClass = "block text-sm font-medium text-text-secondary mb-1";

    const calismaDisi = selectedDurum === 'izinli' || selectedDurum === 'raporlu' || selectedDurum === 'gelmedi';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Puantaj Kaydı Düzenle' : 'Puantaj Kaydı Ekle'}
            size="md"
            fullScreenMobile
        >
            <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
                {/* Tarih ve Personel */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Tarih</label>
                        <input
                            type="date"
                            {...register('tarih', { required: 'Tarih zorunlu' })}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>
                            <User size={14} className="inline mr-1" />Personel *
                        </label>
                        <select
                            {...register('personelId', { required: 'Personel seçimi zorunlu' })}
                            className={inputClass}
                            disabled={isEdit}
                        >
                            <option value="">Personel seçin</option>
                            {mockPersonnel.filter(p => p.status === 'active').map(p => (
                                <option key={p.id} value={p.id}>{p.name} - {getRoleLabel(p.role)}</option>
                            ))}
                        </select>
                        {errors.personelId && <span className="text-error text-xs">{errors.personelId.message}</span>}
                    </div>
                </div>

                {/* Durum */}
                <div>
                    <label className={labelClass}>Çalışma Durumu</label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {calismaDurumlari.map(d => (
                            <label
                                key={d.key}
                                className={`flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${watch('durum') === d.key
                                    ? `border-${d.color} bg-${d.color}/10`
                                    : 'border-border-light hover:bg-bg-hover'
                                    }`}
                            >
                                <input
                                    type="radio"
                                    {...register('durum')}
                                    value={d.key}
                                    className="sr-only"
                                />
                                <span className="text-sm">{d.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Çalışma Saatleri (sadece çalıştı/yarım gün için) */}
                {!calismaDisi && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={labelClass}>
                                <Clock size={14} className="inline mr-1" />Başlangıç
                            </label>
                            <input
                                type="time"
                                {...register('baslangic')}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                <Clock size={14} className="inline mr-1" />Bitiş
                            </label>
                            <input
                                type="time"
                                {...register('bitis')}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Mola (saat)</label>
                            <input
                                type="number"
                                step="0.5"
                                {...register('molaSaat')}
                                className={inputClass}
                                placeholder="1"
                            />
                        </div>
                    </div>
                )}

                {/* Not */}
                <div>
                    <label className={labelClass}>Not</label>
                    <textarea
                        {...register('not')}
                        className={`${inputClass} min-h-[60px]`}
                        placeholder={calismaDisi ? 'İzin/rapor sebebi...' : 'Ek not (opsiyonel)...'}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
                    <Button type="button" variant="ghost" onClick={onClose}>İptal</Button>
                    <Button type="submit" variant="primary">
                        {isEdit ? 'Güncelle' : 'Kaydet'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
